import { Component, OnInit, Input, Output, EventEmitter, forwardRef, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';

// Interface para tabela de preço
interface TabelaPreco {
  id: number;
  nome: string;
  preco: number;
}

@Component({
  selector: 'app-seletor-preco',
  templateUrl: './seletor-preco.component.html',
  styleUrls: ['./seletor-preco.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SeletorPrecoComponent),
      multi: true
    }
  ]
})
export class SeletorPrecoComponent implements OnInit, OnChanges, ControlValueAccessor {

  preco: number = null;
  disabled = false;
  tabelaPrecoSelecionada: TabelaPreco = null;
  possuiTabelasPreco = false;

  @Input() tabelasPreco: TabelaPreco[] = [];
  @Input() bloqueiaEdicaoPreco = false;
  @Input() permiteEdicaoPreco = true; // Nova propriedade para controlar edição do preço
  @Input() casasDecimais = 2;
  @Input() id = 'seletor-preco';
  @Input() idCombo = 'combo-precos';
  @Input() placeholder = 'Selecione uma tabela de preço';
  @Input() prefixoMoeda = 'R$ ';
  @Input() separadorMilhar = '.';
  @Input() separadorDecimal = ',';
  @Input() loading = false;

  @Output() precoChange = new EventEmitter();
  @Output() tabelaPrecoChange = new EventEmitter();

  @ViewChild('selectTabelaPreco', { static: false }) selectTabelaPreco: NgSelectComponent;

  constructor() { }

  ngOnInit() {
    this.processarTabelasPreco();
  }

  ngOnChanges(changes) {
    if (changes.tabelasPreco) {
      this.processarTabelasPreco();
    }
  }

  writeValue(obj: any): void {
    this.preco = obj;
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private propagateChange = (_: any) => { };
  private onTouched = () => { };

  processarTabelasPreco(): void {
    this.possuiTabelasPreco = this.tabelasPreco && this.tabelasPreco.length > 0;

    if (this.tabelasPreco && this.tabelasPreco.length === 1) {
      this.alterarPreco(this.tabelasPreco[0].preco);
      this.tabelaPrecoSelecionada = this.tabelasPreco[0];

      this.tabelaPrecoChange.emit(this.tabelaPrecoSelecionada.preco);
    }
  }

  onChangePreco(event: number): void {
    this.alterarPreco(event);
  }

  onChangeTabelaPreco(preco: number): void {
    this.selecionarTabelaPreco(preco);
  }

  alterarPreco(novoPreco: number): void {
    this.preco = novoPreco;
    this.propagateChange(this.preco);
    this.onTouched();

    this.precoChange.emit(this.preco);
  }

  selecionarTabelaPreco(novoPreco: number): void {
    if (novoPreco !== undefined) {
      // Encontra a tabela selecionada pelo preço
      this.tabelaPrecoSelecionada = this.tabelasPreco.find(t => t.preco === novoPreco);
      this.alterarPreco(novoPreco);
      this.tabelaPrecoChange.emit(novoPreco);
    } else {
      this.limparSelecaoTabela();
    }
  }

  limparSelecaoTabela(): void {
    this.tabelaPrecoSelecionada = null;
    this.alterarPreco(null);
  }

  voltarParaSelecaoTabela(): void {
    this.tabelaPrecoSelecionada = null;
    this.preco = null;
    this.propagateChange(null);
    this.precoChange.emit(null);
    setTimeout(() => {
      this.selectTabelaPreco.open();
    }, 10);
  }

  limpar(): void {
    this.tabelaPrecoSelecionada = null;
    this.preco = null;

    if (this.selectTabelaPreco) {
      this.selectTabelaPreco.clearModel();
    }
  }

  public formatarPreco(preco: number): string {
    if (preco == null || preco == undefined) return '';

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: this.casasDecimais,
      maximumFractionDigits: this.casasDecimais
    }).format(preco);
  }

  public abrirComboPrecos(): void {
    if (this.mostrarSeletorTabela && this.selectTabelaPreco) {
      this.selectTabelaPreco.open();
    }
    else if (this.tabelaPrecoSelecionada && this.tabelasPreco && this.tabelasPreco.length > 1) {
      this.voltarParaSelecaoTabela();
    }
  }

  get mostrarInputPreco(): boolean {
    // Mostra input quando não há tabelas OU quando há apenas uma tabela OU quando uma tabela foi selecionada
    return !this.possuiTabelasPreco ||
      (this.tabelasPreco && this.tabelasPreco.length === 1) ||
      (this.tabelaPrecoSelecionada !== null);
  }

  get mostrarSeletorTabela(): boolean {
    // Mostra combobox apenas quando há 2 ou mais tabelas de preço E nenhuma tabela foi selecionada
    return this.possuiTabelasPreco &&
      this.tabelasPreco &&
      this.tabelasPreco.length > 1 &&
      this.tabelaPrecoSelecionada === null;
  }

  get precoDesabilitado(): boolean {
    return this.disabled || this.bloqueiaEdicaoPreco || !this.permiteEdicaoPreco;
  }

  get labelTabelaSelecionada(): string {
    if (this.tabelaPrecoSelecionada) {
      return this.formatarPreco(this.tabelaPrecoSelecionada.preco);
    }
    return '';
  }
}
